import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * Converts an image file to WebP format
 * @param {File} file - The image file to convert
 * @returns {Promise<Blob>} - Promise that resolves with the WebP Blob
 */
export const convertToWebP = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof Blob)) {
      return reject(new Error("Invalid file provided"));
    }

    if (!file.type.match("image.*")) {
      return reject(new Error("File is not an image"));
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas dimensions
        const maxDimension = 1200; // Max width/height
        let width = img.width;
        let height = img.height;

        // Resize if image is too large
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height / width) * maxDimension);
            width = maxDimension;
          } else {
            width = Math.round((width / height) * maxDimension);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw image with new dimensions
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP (0.8 quality is a good balance between size and quality)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to convert image to WebP"));
            }
          },
          "image/webp",
          0.8
        );
      };

      img.onerror = (error) => {
        console.error("Image load error:", {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          error,
        });
        reject(
          new Error(
            `Failed to process image: ${file.name}. Please try another image.`
          )
        );
      };

      try {
        img.src = event.target.result;
      } catch (error) {
        reject(new Error(`Invalid image data: ${error.message}`));
      }
    };

    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      reject(new Error("Failed to read the image file. It may be corrupted."));
    };

    try {
      reader.readAsDataURL(file);
    } catch (error) {
      reject(new Error(`Failed to process image: ${error.message}`));
    }
  });
};

/**
 * Uploads an image to Firebase Storage with WebP conversion
 * @param {File} file - The image file to upload
 * @param {string} userId - The user's UID
 * @param {string} path - The path to store the image (e.g., 'payment-methods')
 * @returns {Promise<{url: string, path: string}>} - Object containing the download URL and storage path
 */
export const uploadImage = async (file, userId, path = "payment-methods") => {
  try {
    const storage = getStorage();

    // Convert image to WebP
    const webpBlob = await convertToWebP(file);

    // Generate a unique filename with timestamp
    const timestamp = Date.now();
    const fileExt = "webp";
    const fileName = `${path.split("/").pop()}-${timestamp}.${fileExt}`;
    const storagePath = `${path}/${userId}/${fileName}`;

    // Create a reference to the file
    const storageRef = ref(storage, storagePath);

    // Upload the WebP image
    await uploadBytes(storageRef, webpBlob, {
      contentType: "image/webp",
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);

    return {
      url: downloadURL,
      path: storagePath,
    };
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
};

/**
 * Handles file selection and upload
 * @param {Event} event - The file input change event
 * @param {string} userId - The user's UID
 * @param {string} path - The storage path
 * @returns {Promise<{url: string, path: string}>} - Object containing the download URL and storage path
 */
export const handleFileUpload = async (
  event,
  userId,
  path = "payment-methods"
) => {
  const file = event.target.files[0];
  if (!file) return null;

  // Check file type
  if (!file.type.match("image.*")) {
    throw new Error("Please select an image file");
  }

  // Check file size (5MB max)
  if (file.size > 5 * 1024 * 1024) {
    throw new Error("Image must be less than 5MB");
  }

  return await uploadImage(file, userId, path);
};
